import { IAttachmentDTO } from '@user/store/attachments'
import React, { useCallback, useMemo, useState } from 'react'
import { FilePreview, FilePreviewMimeTypeIcon } from '@workwave-tidal/lab'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import MoreVert from '@mui/icons-material/MoreVert'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useLocalizationDateFormat } from '@user/features/Localization/useLocalizationDateFormat'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { useAttachmentData } from '../hooks'
import Checkbox from '@mui/material/Checkbox'
import {
  AgreementAttachmentAcceptFileTypes,
  AttachmentMessages,
} from '../constants'
import { LightTooltip } from '@sales-center/components'

type Props = {
  attachment: IAttachmentDTO
  canUpdate: boolean
  isSelectionEnable: boolean
  checked: boolean
  handleCheckChange: () => void
}

export function SaleCenterAttachmentsListItem({
  attachment,
  canUpdate,
  isSelectionEnable,
  checked,
  handleCheckChange,
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const {
    handleDeleteScAttachment,
    handleDownloadScAttachment,
    getMimeTypeFromFileName,
  } = useAttachmentData()

  const { formatDate } = useLocalizationDateFormat()

  const handleClose = useCallback(async () => {
    setAnchorEl(null)
  }, [setAnchorEl])

  const disabled = useMemo(
    () => !canUpdate || ['uploading', 'deleting'].includes(attachment.status),
    [attachment, canUpdate],
  )

  const disableAgreementButton = useMemo(() => {
    if (
      isSelectionEnable &&
      !AgreementAttachmentAcceptFileTypes.includes(
        getMimeTypeFromFileName(attachment.displayName),
      )
    ) {
      return true
    }
    return false
  }, [getMimeTypeFromFileName, attachment, isSelectionEnable])

  const onDownloadClick = useCallback(async () => {
    handleDownloadScAttachment(
      attachment.id,
      attachment.displayName,
      attachment.isCompletedFormPdf,
    )
    handleClose()
  }, [handleDownloadScAttachment, handleClose, attachment])

  return (
    <Box
      sx={{
        mb: 0.8,
        display: 'flex',
      }}
    >
      {isSelectionEnable ? (
        <LightTooltip
          title={
            disableAgreementButton
              ? AttachmentMessages.InvalidAgreementAttachmentType
              : ''
          }
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Checkbox
              disabled={disableAgreementButton || disabled}
              checked={checked}
              onChange={handleCheckChange}
              sx={{
                height: 42,
                width: 42,
              }}
              data-test-id="sale-center-attachment-checkbox"
            />
          </Box>
        </LightTooltip>
      ) : null}
      <Box sx={{ width: '100%' }}>
        <FilePreview
          disabled={disabled}
          loading={['uploading', 'deleting'].includes(attachment.status)}
          error={attachment.status == 'error'}
          icon={
            <FilePreviewMimeTypeIcon
              preset={getMimeTypeFromFileName(attachment.displayName)}
            />
          }
          title={attachment.displayName}
          tooltip=""
          subtitle={
            <Box
              color={'#0E283FB2'}
              display={'flex'}
              flexDirection={'row'}
              gap={0.8}
            >
              <Typography variant="body2">
                {formatDate(attachment.createdDate)}
              </Typography>
              {attachment?.fileSize ? (
                <Typography variant="body2">
                  {' '}
                  - {attachment?.fileSize}
                </Typography>
              ) : null}
            </Box>
          }
          variant="standard"
          actionsVisibility="always"
          actions={[
            ...(!attachment.isCompletedFormPdf
              ? [
                  <ActionBarItem
                    disableInteraction={disabled}
                    onClick={() => {
                      handleDeleteScAttachment(attachment.id)
                      if (checked) {
                        handleCheckChange()
                      }
                    }}
                    key="add"
                    id="add"
                    label="Add"
                    icon={<DeleteOutline />}
                    data-test-id={`scAttachmentDeleteBtn-${attachment.id}`}
                  />,
                ]
              : []),
            <ActionBarItem
              disableInteraction={disabled}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              key="More"
              id="more-button"
              label="More"
              icon={<MoreVert />}
              data-test-id={`scAttachmentMoreBtn-${attachment.id}`}
            />,
          ]}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem
            onClick={onDownloadClick}
            data-test-id="scAttachmentDownloadOption"
          >
            Download
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  )
}
